/** Verificările picate ale unei soluții de structură, explicate la hover.
 *
 * «13 picate» spune cât, nu ce. Textul de aici — categoriile picate, cu câte
 * verificări are fiecare — se pune pe `title`, oriunde apare cifra: pe cardul
 * capitolului «Soluția» din Acasă, pe pastila din panoul soluției, în lista
 * de soluții a structurii și pe fișă. Într-un singur loc, ca și scorul
 * (`src/scor.ts`).
 *
 * Categoriile vin de la server gata numărate (`picate_categorii`, din
 * `optimizare.categorii_picate`) sau se scot din `stare_finala.rezumat`
 * acolo unde ecranul are starea întreagă.
 */

import { numar, t, tn } from "./i18n";

/** o regulă picată a planului de situație / o structură nerezolvată a
 *  arhitecturii — forma `summary.abateri` (v. `positioning_plan/service._summary`) */
export type Abatere = {
  titlu?: string | null;
  valoare?: number | null;
  prag?: number | null;
  um?: string | null;
  articol?: string | null;
};

export type CategoriePicata = {
  tip?: string;
  /** numele lizibil al categoriei; lipsește → se scrie cheia tehnică */
  eticheta?: string | null;
  n: number;
};

type StareCuPicate = {
  nr_picate?: number | null;
  /** tip → câte verificări au picat în categoria aia */
  rezumat?: Record<string, number> | null;
  /** lista (tăiată pe server) — de aici se ia doar ETICHETA fiecărui tip */
  picate?: { tip?: string; eticheta?: string }[] | null;
} | null | undefined;

/** Categoriile picate dintr-o stare finală, cele mai multe întâi. */
export function categoriiDinStare(stare: StareCuPicate): CategoriePicata[] {
  const etichete = new Map<string, string>();
  for (const p of stare?.picate ?? [])
    if (p.tip && p.eticheta && !etichete.has(p.tip)) etichete.set(p.tip, p.eticheta);
  return Object.entries(stare?.rezumat ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tip, n]) => ({ tip, eticheta: etichete.get(tip) ?? null, n }));
}

function randAbatere(a: Abatere): string {
  let s = a.titlu || "?";
  if (a.valoare != null && a.prag != null)
    s += t("picate.abatere_cifre", {
      valoare: numar(a.valoare, { maximumFractionDigits: 2 }),
      prag: numar(a.prag, { maximumFractionDigits: 2 }),
      um: a.um ? ` ${a.um}` : "",
    });
  if (a.articol) s += ` · ${a.articol}`;
  return s;
}

/** Textul de hover pentru «{nr} picate»: câte, și care.
 *
 * Structura vine pe CATEGORII (tip → câte); planul de situație și arhitectura
 * vin cu ABATERILE una câte una (regula, valoarea, pragul). Se scrie ce există. */
export function explicaPicatele(nr?: number | null,
                                categorii?: CategoriePicata[] | null,
                                abateri?: Abatere[] | null): string {
  if (nr == null) return "";
  if (nr === 0) return t("picate.toate_trec");
  const randuri = (categorii ?? [])
    .filter((c) => c.n > 0)
    .map((c) => t("picate.rand", { n: c.n, eticheta: c.eticheta || c.tip || "?" }));
  if (!randuri.length) randuri.push(...(abateri ?? []).map(randAbatere));
  if (!randuri.length) return `${tn("picate.titlu#n", nr)}\n${t("picate.fara_detaliu")}`;
  return [tn("picate.titlu#n", nr), ...randuri].join("\n");
}
